import { useState, useRef, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Cloud, HardDrive, ExternalLink, FileText, CheckCircle2, AlertCircle, Upload, FolderPlus, Folder, Trash2, Camera, Share2, User as UserIcon, Search, ChevronRight, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import type { DriveFileShare, DriveFileShareWithOwner, FriendWithUser } from "@shared/schema";
import { Link } from "wouter";

type DriveConnection = {
  id: string;
  email: string;
  connected: boolean;
};

type DriveTreeItem = {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  size?: number;
  webViewLink?: string;
  thumbnailLink?: string;
  modifiedTime?: string;
  isAppManaged?: boolean;
  dbId?: string;
};

type DriveTreeResponse = {
  parentId: string;
  items: DriveTreeItem[];
  nextPageToken?: string;
};

export default function Drive() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [cameraOptionsDialogOpen, setCameraOptionsDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [sharingDriveFileId, setSharingDriveFileId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("my-files");
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const [currentParentId, setCurrentParentId] = useState("root");
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([]);
  const [pageToken, setPageToken] = useState<string | undefined>();
  const [treeItems, setTreeItems] = useState<DriveTreeItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();

  // Helper function for flexible friend search
  function matchesSearch(searchTerm: string, firstName: string, lastName: string, email: string): boolean {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase().trim();
    const first = (firstName || '').toLowerCase();
    const last = (lastName || '').toLowerCase();
    const fullName = `${first} ${last}`;
    const combinedName = `${first}${last}`;
    const emailLower = (email || '').toLowerCase();
    
    if (first.includes(search) || last.includes(search) || fullName.includes(search) || emailLower.includes(search)) {
      return true;
    }
    
    if (combinedName.includes(search)) {
      return true;
    }
    
    return combinedName.includes(search);
  }

  const { data: connection, isLoading: connectionLoading, isError: connectionIsError, error: connectionError } = useQuery<DriveConnection>({
    queryKey: ["/api/google-drive/connection"],
  });

  const { data: tree, isLoading: filesLoading, isError: filesIsError, error: filesError } = useQuery<DriveTreeResponse>({
    queryKey: ["/api/google-drive/tree", currentParentId, pageToken ?? "first"],
    enabled: connection?.connected === true,
    queryFn: async () => {
      const params = new URLSearchParams({ parentId: currentParentId });
      if (pageToken) params.set("pageToken", pageToken);
      const response = await fetch(`/api/google-drive/tree?${params.toString()}`, {
        credentials: "include",
        cache: "no-store",
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(body.message || body.error || response.statusText || "Failed to load Drive files") as Error & { requiresReconnect?: boolean };
        error.requiresReconnect = body.requiresReconnect === true;
        throw error;
      }
      return body;
    },
  });
  useEffect(() => {
    if (!tree) return;
    setTreeItems((previous) => pageToken
      ? [...previous, ...tree.items.filter((item) => !previous.some((existing) => existing.id === item.id))]
      : tree.items);
    setNextPageToken(tree.nextPageToken);
  }, [tree, pageToken]);
  const files = useMemo(
    () => [...treeItems].sort((a, b) => Number(b.isFolder) - Number(a.isFolder) || a.name.localeCompare(b.name)),
    [treeItems],
  );
  const actionableFiles = useMemo(
    () => files.filter((file) => !file.isFolder && file.isAppManaged === true && !!file.dbId),
    [files],
  );
  const requiresReconnect = (filesError as (Error & { requiresReconnect?: boolean }) | null)?.requiresReconnect === true;

  const { data: friends = [] } = useQuery<FriendWithUser[]>({
    queryKey: ["/api/friends"],
  });

  const { data: sharedWithMe = [], isLoading: isLoadingShared } = useQuery<DriveFileShareWithOwner[]>({
    queryKey: ["/api/drive-files/shared-with-me"],
  });

  const { data: currentFileShares = [], isLoading: isLoadingShares } = useQuery<DriveFileShare[]>({
    queryKey: ["/api/drive-files", sharingDriveFileId, "shares"],
    enabled: !!sharingDriveFileId,
  });

  // Filter friends by search query
  const filteredFriends = useMemo(() => {
    return friends.filter(friendship => matchesSearch(
      friendSearchQuery,
      friendship.friend.firstName || '',
      friendship.friend.lastName || '',
      friendship.friend.email || ''
    ));
  }, [friends, friendSearchQuery]);

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", "/api/google-drive/disconnect");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/connection"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/tree"] });
      toast({
        title: "Disconnected",
        description: "Successfully disconnected from Google Drive",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect from Google Drive",
        variant: "destructive",
      });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (data: { fileName: string; fileData: string; mimeType: string }) => {
      return await apiRequest("POST", "/api/google-drive/upload", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/tree"] });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      toast({
        title: "File Uploaded",
        description: "File successfully uploaded to Google Drive",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload file to Google Drive",
        variant: "destructive",
      });
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apiRequest("POST", "/api/google-drive/folders", { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/tree"] });
      setFolderDialogOpen(false);
      setFolderName("");
      toast({
        title: "Folder Created",
        description: "Folder successfully created in Google Drive",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Folder Creation Failed",
        description: error.message || "Failed to create folder in Google Drive",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (driveFileId: string) => {
      return await apiRequest("DELETE", `/api/google-drive/files/${driveFileId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/tree"] });
      toast({
        title: "File Deleted",
        description: "File successfully deleted from Google Drive",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete file from Google Drive",
        variant: "destructive",
      });
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: async (driveFileIds: string[]) => {
      return await apiRequest("POST", "/api/google-drive/batch-delete", { driveFileIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/google-drive/tree"] });
      setSelectedFileIds([]);
      toast({
        title: "Files Deleted",
        description: `${selectedFileIds.length} file(s) successfully deleted from Google Drive`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Batch Delete Failed",
        description: error.message || "Failed to delete files from Google Drive",
        variant: "destructive",
      });
    },
  });

  const shareDriveFileMutation = useMutation({
    mutationFn: async ({ driveFileId, sharedWithId }: { driveFileId: string; sharedWithId: string }) => {
      return await apiRequest("POST", `/api/drive-files/${driveFileId}/share`, { sharedWithId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drive-files", sharingDriveFileId, "shares"] });
      toast({
        title: "File Shared",
        description: "File has been shared successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Share Failed",
        description: error.message || "Could not share file",
        variant: "destructive",
      });
    },
  });

  const unshareDriveFileMutation = useMutation({
    mutationFn: async (shareId: string) => {
      return await apiRequest("DELETE", `/api/drive-files/shares/${shareId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drive-files", sharingDriveFileId, "shares"] });
      toast({
        title: "Share Removed",
        description: "File share has been removed",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Unshare Failed",
        description: error.message || "Could not remove share",
        variant: "destructive",
      });
    },
  });

  const handleConnect = () => {
    // Forward a returnTo param (e.g. set by the Attach Document dialog) through
    // the OAuth flow so the callback can send the user back where they started.
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    window.location.href = returnTo
      ? `/api/google-drive/connect?returnTo=${encodeURIComponent(returnTo)}`
      : "/api/google-drive/connect";
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadDialogOpen(true);
    }
  };

  const handleCameraSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadDialogOpen(true);
      setCameraOptionsDialogOpen(false);
    }
  };

  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadDialogOpen(true);
      setCameraOptionsDialogOpen(false);
    }
  };

  const handleCameraButtonClick = () => {
    setCameraOptionsDialogOpen(true);
  };

  const handleTakePictureOption = () => {
    cameraInputRef.current?.click();
  };

  const handleChooseFromGalleryOption = () => {
    galleryInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result?.toString().split(',')[1];
      if (base64) {
        uploadMutation.mutate({
          fileName: selectedFile.name,
          fileData: base64,
          mimeType: selectedFile.type || 'application/octet-stream',
        });
      }
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCreateFolder = () => {
    if (!folderName.trim()) return;
    createFolderMutation.mutate(folderName.trim());
  };

  const handleDelete = (driveFileId: string) => {
    if (confirm("Are you sure you want to delete this file?")) {
      deleteMutation.mutate(driveFileId);
    }
  };

  const handleBatchDelete = () => {
    if (selectedFileIds.length === 0) return;
    if (confirm(`Are you sure you want to delete ${selectedFileIds.length} file(s)?`)) {
      batchDeleteMutation.mutate(selectedFileIds);
    }
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFileIds.length === actionableFiles.length) {
      setSelectedFileIds([]);
    } else {
      setSelectedFileIds(actionableFiles.map(f => f.id));
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("folder")) {
      return <Folder className="w-5 h-5 text-yellow-500" data-testid="icon-folder" />;
    }
    return <FileText className="w-5 h-5 text-blue-500" data-testid="icon-file" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      return format(new Date(dateString), "PP");
    } catch {
      return dateString;
    }
  };

  const openFolder = (folder: DriveTreeItem) => {
    setBreadcrumbs((previous) => [...previous, { id: folder.id, name: folder.name }]);
    setCurrentParentId(folder.id);
    setSelectedFileIds([]);
    setPageToken(undefined);
    setTreeItems([]);
    setNextPageToken(undefined);
  };

  const navigateToBreadcrumb = (index: number) => {
    const target = index < 0 ? { id: "root" } : breadcrumbs[index];
    setCurrentParentId(target.id);
    setBreadcrumbs((previous) => index < 0 ? [] : previous.slice(0, index + 1));
    setSelectedFileIds([]);
    setPageToken(undefined);
    setTreeItems([]);
    setNextPageToken(undefined);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" data-testid="text-page-title">Google Drive Storage</h1>
        <p className="text-muted-foreground">Browse and manage files in your connected Google Drive</p>
        <nav className="mt-3 flex items-center gap-2" aria-label="Storage source">
          <Button variant="outline" size="sm" asChild data-testid="link-app-storage">
            <Link href="/media">App Storage</Link>
          </Button>
          <Button variant="default" size="sm" asChild data-testid="link-google-drive">
            <Link href="/drive">Google Drive</Link>
          </Button>
        </nav>
      </div>

      <Card data-testid="card-connection-status">
        <CardHeader>
          <CardTitle>Connection Status</CardTitle>
        </CardHeader>
        <CardContent>
          {connectionIsError ? (
            <Alert variant="destructive" data-testid="alert-connection-error">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Failed to check Drive connection: {connectionError?.message}
              </AlertDescription>
            </Alert>
          ) : connectionLoading ? (
            <p className="text-muted-foreground" data-testid="text-loading">Loading connection status...</p>
          ) : connection?.connected ? (
            <div className="space-y-4">
              <Alert data-testid="alert-connected">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Connected as <strong data-testid="text-email">{connection.email}</strong>
                </AlertDescription>
              </Alert>
              <Button 
                variant="outline" 
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                data-testid="button-disconnect"
              >
                {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert data-testid="alert-not-connected">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Connect to Google Drive to store your files in a dedicated "GoalVia Files" folder
                </AlertDescription>
              </Alert>
              <Button 
                onClick={handleConnect}
                data-testid="button-connect"
              >
                <Cloud className="w-4 h-4 mr-2" />
                Connect to Google Drive
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {connection?.connected && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="tabs-drive">
          <TabsList className="mb-4" data-testid="tabs-list-drive">
            <TabsTrigger value="my-files" data-testid="tab-my-files">My Files</TabsTrigger>
            <TabsTrigger value="shared-with-me" data-testid="tab-shared-with-me">Shared with me</TabsTrigger>
          </TabsList>

          <TabsContent value="my-files" data-testid="tab-content-my-files">
            <Card data-testid="card-files">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>GoalVia Files</CardTitle>
                    <CardDescription>Files stored in your GoalVia folder</CardDescription>
                  </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setFolderDialogOpen(true)}
                  data-testid="button-create-folder"
                  variant="outline"
                  className="flex-shrink-0"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Create Folder</span>
                  <span className="sm:hidden">Folder</span>
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-upload"
                  className="flex-shrink-0"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Upload File</span>
                  <span className="sm:hidden">Upload</span>
                </Button>
                <Button
                  onClick={handleCameraButtonClick}
                  data-testid="button-upload-picture"
                  className="flex-shrink-0 bg-red-600 hover:bg-red-700 text-white"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Take Picture</span>
                  <span className="sm:hidden">Camera</span>
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="input-file"
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleCameraSelect}
                  data-testid="input-camera"
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleGallerySelect}
                  data-testid="input-gallery"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm" aria-label="Drive folder path" data-testid="drive-breadcrumbs">
              {breadcrumbs.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => navigateToBreadcrumb(breadcrumbs.length - 2)} data-testid="button-folder-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              <Button variant="link" size="sm" onClick={() => navigateToBreadcrumb(-1)} data-testid="button-breadcrumb-root">GoalVia Files</Button>
              {breadcrumbs.map((crumb, index) => (
                <span key={`${crumb.id}-${index}`} className="flex items-center">
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <Button variant="link" size="sm" onClick={() => navigateToBreadcrumb(index)} data-testid={`button-breadcrumb-${crumb.id}`}>
                    {crumb.name}
                  </Button>
                </span>
              ))}
            </nav>
            {requiresReconnect ? (
              <Alert variant="destructive" data-testid="alert-drive-reconnect">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Reconnect Google Drive</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>Browsing folders requires expanded Google Drive file-list permission. Reconnect to grant that permission. This does not request access to read file content.</p>
                  <Button onClick={handleConnect} data-testid="button-reconnect-google-drive">
                    <Cloud className="w-4 h-4 mr-2" />
                    Reconnect Google Drive
                  </Button>
                </AlertDescription>
              </Alert>
            ) : filesIsError ? (
              <Alert variant="destructive" data-testid="alert-files-error">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  Failed to load Drive files: {filesError?.message}
                </AlertDescription>
              </Alert>
            ) : filesLoading ? (
              <p className="text-muted-foreground" data-testid="text-loading-files">Loading files...</p>
            ) : files.length === 0 ? (
              <div className="text-center py-12">
                <HardDrive className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-2" data-testid="text-no-files">No files yet</p>
                <p className="text-sm text-muted-foreground">Upload your first file or create a folder to get started</p>
              </div>
            ) : (
              <>
                {selectedFileIds.length > 0 && (
                  <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between" data-testid="bulk-actions-bar">
                    <span className="text-sm font-medium">
                      {selectedFileIds.length} file(s) selected
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBatchDelete}
                      disabled={batchDeleteMutation.isPending}
                      data-testid="button-batch-delete"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {batchDeleteMutation.isPending ? "Deleting..." : "Delete Selected"}
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="table-files">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left w-12">
                          <Checkbox
                            checked={actionableFiles.length > 0 && selectedFileIds.length === actionableFiles.length}
                            onCheckedChange={toggleSelectAll}
                            disabled={actionableFiles.length === 0}
                            data-testid="checkbox-select-all"
                          />
                        </th>
                        <th className="p-3 text-left">Icon</th>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-left">Type</th>
                        <th className="p-3 text-left">Modified Date</th>
                        <th className="p-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => (
                        <tr key={file.id} className="border-t hover:bg-muted/30" data-testid={`row-file-${file.id}`}>
                          <td className="p-3">
                            {!file.isFolder && file.isAppManaged === true && file.dbId ? (
                              <Checkbox
                                checked={selectedFileIds.includes(file.id)}
                                onCheckedChange={() => toggleFileSelection(file.id)}
                                data-testid={`checkbox-file-${file.id}`}
                              />
                            ) : null}
                          </td>
                          <td className="p-3">
                            {getFileIcon(file.mimeType)}
                          </td>
                          <td className="p-3 font-medium" data-testid={`text-name-${file.id}`}>
                            {file.isFolder ? (
                              <Button variant="link" className="h-auto p-0 font-medium" onClick={() => openFolder(file)} data-testid={`button-open-folder-${file.id}`}>
                                {file.name}
                              </Button>
                            ) : file.name}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground" data-testid={`text-type-${file.id}`}>
                            {file.isFolder ? 'Folder' : file.mimeType.split('/').pop() || 'File'}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground" data-testid={`text-date-${file.id}`}>
                            {formatDate(file.modifiedTime)}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {file.isFolder ? (
                                <Button variant="outline" size="sm" onClick={() => openFolder(file)} data-testid={`button-expand-folder-${file.id}`}>
                                  <Folder className="w-4 h-4 mr-2" /> Open folder
                                </Button>
                              ) : file.webViewLink && (
                                <Button variant="outline" size="sm" onClick={() => window.open(file.webViewLink, "_blank")} data-testid={`button-open-${file.id}`}>
                                  <ExternalLink className="w-4 h-4 mr-2" /> Open
                                </Button>
                              )}
                              {!file.isFolder && file.isAppManaged === true && file.dbId && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSharingDriveFileId(file.dbId!)}
                                    data-testid={`button-share-${file.id}`}
                                  >
                                    <Share2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDelete(file.id)}
                                    disabled={deleteMutation.isPending}
                                    data-testid={`button-delete-${file.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {nextPageToken && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setPageToken(nextPageToken)}
                      disabled={filesLoading}
                      data-testid="button-load-more-drive-files"
                    >
                      {filesLoading ? "Loading..." : "Load more"}
                    </Button>
                  </div>
                )}
              </>
            )}
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="shared-with-me" data-testid="tab-content-shared-with-me">
            <div data-testid="container-shared-drive-files">
              {isLoadingShared ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                    <p className="text-muted-foreground" data-testid="text-loading-shared">Loading shared files...</p>
                  </CardContent>
                </Card>
              ) : sharedWithMe.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Share2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground" data-testid="text-no-shared">No files have been shared with you</p>
                    <p className="text-sm text-muted-foreground">When others share files with you, they will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="table-shared-drive-files">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left">Icon</th>
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Type</th>
                            <th className="p-3 text-left">Shared By</th>
                            <th className="p-3 text-left">Modified Date</th>
                            <th className="p-3 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sharedWithMe.map((item) => (
                            <tr key={item.share.id} className="border-t hover:bg-muted/30" data-testid={`row-shared-file-${item.share.id}`}>
                              <td className="p-3">
                                {getFileIcon(item.driveFile.mimeType)}
                              </td>
                              <td className="p-3 font-medium" data-testid={`text-shared-name-${item.share.id}`}>
                                {item.driveFile.name}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground" data-testid={`text-shared-type-${item.share.id}`}>
                                {item.driveFile.mimeType.includes('folder') ? 'Folder' : item.driveFile.mimeType.split('/').pop() || 'File'}
                              </td>
                              <td className="p-3" data-testid={`text-shared-owner-${item.share.id}`}>
                                <div className="flex items-center gap-2">
                                  <UserIcon className="w-4 h-4 text-muted-foreground" />
                                  <span>{item.owner.firstName || item.owner.email || item.owner.username || "Unknown"}</span>
                                </div>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground" data-testid={`text-shared-date-${item.share.id}`}>
                                {item.driveFile.modifiedTime ? format(new Date(item.driveFile.modifiedTime), "PP") : "—"}
                              </td>
                              <td className="p-3">
                                {item.driveFile.webViewLink && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(item.driveFile.webViewLink!, "_blank")}
                                    data-testid={`button-open-shared-${item.share.id}`}
                                  >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Open
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Share Drive File Dialog */}
      <Dialog open={sharingDriveFileId !== null} onOpenChange={(open) => {
        if (!open) {
          setSharingDriveFileId(null);
          setFriendSearchQuery("");
        }
      }}>
        <DialogContent data-testid="dialog-share-drive-file" >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Drive File
            </DialogTitle>
            <DialogDescription>
              Select friends to share this file with. They will be able to view the file.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoadingShares ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : friends.length === 0 ? (
              <p className="text-muted-foreground text-center py-4" data-testid="text-no-friends">No friends to share with</p>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search friends by name or email..."
                    value={friendSearchQuery}
                    onChange={(e) => setFriendSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="input-search-friends"
                  />
                </div>
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {filteredFriends.map((friendship) => {
                    const friend = friendship.friend;
                    const existingShare = currentFileShares.find(s => s.sharedWithId === friend.id);
                    const isShared = !!existingShare;
                    const isPending = shareDriveFileMutation.isPending || unshareDriveFileMutation.isPending;
                    
                    return (
                      <div
                        key={friend.id}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                        data-testid={`share-friend-row-${friend.id}`}
                      >
                        <Checkbox
                          id={`share-${friend.id}`}
                          checked={isShared}
                          disabled={isPending}
                          onCheckedChange={(checked) => {
                            if (checked && sharingDriveFileId) {
                              shareDriveFileMutation.mutate({ driveFileId: sharingDriveFileId, sharedWithId: friend.id });
                            } else if (!checked && existingShare) {
                              unshareDriveFileMutation.mutate(existingShare.id);
                            }
                          }}
                          data-testid={`checkbox-share-${friend.id}`}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            {friend.profileImageUrl ? (
                              <img src={friend.profileImageUrl} alt="" className="w-8 h-8 rounded-full" />
                            ) : (
                              <UserIcon className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" data-testid={`text-share-friend-name-${friend.id}`}>
                              {friend.firstName ? `${friend.firstName} ${friend.lastName || ""}`.trim() : friend.email || friend.username}
                            </p>
                            {friend.email && friend.firstName && (
                              <p className="text-xs text-muted-foreground truncate">{friend.email}</p>
                            )}
                          </div>
                        </div>
                        {isShared && (
                          <Badge variant="secondary" className="text-xs" data-testid={`badge-shared-${friend.id}`}>Shared</Badge>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSharingDriveFileId(null); setFriendSearchQuery(""); }} data-testid="button-close-share-dialog">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent data-testid="dialog-upload" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>
              Upload a file to your GoalVia folder in Google Drive
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm font-medium mb-2">Selected file:</p>
            <p className="text-sm text-muted-foreground" data-testid="text-selected-file">
              {selectedFile?.name || "No file selected"}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setSelectedFile(null);
              }}
              data-testid="button-cancel-upload"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
              data-testid="button-confirm-upload"
            >
              {uploadMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent data-testid="dialog-create-folder" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
            <DialogDescription>
              Create a new folder in your GoalVia files
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              data-testid="input-folder-name"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setFolderDialogOpen(false);
                setFolderName("");
              }}
              data-testid="button-cancel-folder"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!folderName.trim() || createFolderMutation.isPending}
              data-testid="button-confirm-folder"
            >
              {createFolderMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera Options Dialog */}
      <Dialog open={cameraOptionsDialogOpen} onOpenChange={setCameraOptionsDialogOpen}>
        <DialogContent data-testid="dialog-camera-options" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Picture</DialogTitle>
            <DialogDescription>
              Choose how you'd like to add a picture
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              onClick={handleTakePictureOption}
              className="w-full h-16 text-lg"
              data-testid="button-take-picture"
            >
              <Camera className="w-5 h-5 mr-3" />
              Take Picture
            </Button>
            <Button
              onClick={handleChooseFromGalleryOption}
              variant="outline"
              className="w-full h-16 text-lg"
              data-testid="button-choose-gallery"
            >
              <Upload className="w-5 h-5 mr-3" />
              Choose from Gallery
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCameraOptionsDialogOpen(false)}
              data-testid="button-cancel-camera"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
